
import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Notification, notificationRepository } from 'api/notificationRepository';
import { useAuth } from 'contexts/authContext';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotificationDetailCard from 'views/admin/notification/Component/NotificationDetailCard';

const NotificationDetail: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useAuth();

  const location = useLocation();
  const id = location.state?.id || null;
  const [notification, setNotification] = useState<Notification | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(true);

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertStatus, setCenterAlertStatus] = useState<"success" | "error" | "info" | "warning">("info");
  const [centerAlertMessage, setCenterAlertMessage] = useState("");

  useEffect(() => {
    const id = location.state?.id || null;
    if (!id) return;

    (async () => {
      try {
        const data = await notificationRepository.getNotificationById(id);
        setNotification(data);
      } catch (err) {
        showCenteredAlert("error", "Failed to load notification details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const showCenteredAlert = (status: "success" | "error" | "info" | "warning", message: string) => {
    setCenterAlertStatus(status);
    setCenterAlertMessage(message);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 2000);
  };

  const handleDelete = async () => {
    if (role === "DeptUser") return

    if (!id) return;

    try {
      await notificationRepository.deleteNotification(id);
      showCenteredAlert("success", "Notification deleted successfully.");
      setTimeout(() => navigate('/admin/notification/NotificationList'), 2500);
    } catch (err) {
      showCenteredAlert("error", "Failed to delete notification.");
    } finally {
      onClose();
    }
  };

  return (
    <Box p={6} mt={16}>
      <Flex justify="flex-end" >
        <IconButton
          aria-label="Close"
          icon={<CloseIcon />}
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/notification/NotificationList')}
        />
      </Flex>

      <NotificationDetailCard
        notification={notification || undefined}
        onDelete={role !== "DeptUser" && role !== "OrgAdmin" && role !== "Vendor" ? onOpen : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Notification
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this notification? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Centered Alert */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            bg={centerAlertStatus === 'success' ? 'green.50' : undefined}
            borderRadius="14px"
            boxShadow="lg"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} color={centerAlertStatus === 'success' ? 'green.600' : undefined} />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Box>
  );
};

export default NotificationDetail;
